/**
 * Shared utilities for encounter detail components.
 * Extracted from BillingEncounterRow to avoid duplication.
 */

import React from 'react';
import {
  EventNote as EncounterIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Videocam as VideoIcon,
} from '@mui/icons-material';
import type { Theme } from '@mui/material';
import type { OrgBillingEncounterDetailResponse } from '@store/generated/billing';

import { formatDateOnly } from '../../utils';

// ---------------------------------------------------------------------------
// Working billing claim types (matches WorkingBillingClaimResponse shape)
// ---------------------------------------------------------------------------

export interface WorkingClaimLineItem {
  code_id?: string | null;
  charge_cents?: number | null;
  modifiers?: string[];
  modifier_sources?: Record<string, 'provider_selected' | 'ai_recommended'>;
  units?: number | null;
  source?: 'provider_selected' | 'ai_recommended' | null;
}

export interface WorkingBillingClaim {
  line_items?: WorkingClaimLineItem[];
  diagnosis_codes?: string[];
  diagnosis_code_sources?: Record<string, 'provider_selected' | 'ai_recommended'>;
  place_of_service?: string | null;
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export const formatDate = (dateString: string | null | undefined): string =>
  formatDateOnly(dateString, { month: 'short', day: 'numeric', year: 'numeric' }, '—');

export const formatTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

export const formatCents = (cents: number): string => {
  const dollars = cents / 100;
  return `$${dollars.toFixed(2)}`;
};

// ---------------------------------------------------------------------------
// Name helpers
// ---------------------------------------------------------------------------

export function getNameParts(fullName: string | null | undefined): { firstName: string; lastName: string } {
  if (!fullName) return { firstName: '', lastName: '' };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function getPatientDisplayName(encounter: OrgBillingEncounterDetailResponse): string {
  const info = encounter.patient_info as { first_name?: string; last_name?: string; preferred_name?: string } | null;
  if (info?.preferred_name) return info.preferred_name;
  if (info?.first_name) {
    return [info.first_name, info.last_name].filter(Boolean).join(' ');
  }
  return encounter.patient_name || 'Patient';
}

// ---------------------------------------------------------------------------
// Modality icon
// ---------------------------------------------------------------------------

export const getModalityIcon = (modality: string | null | undefined): React.ReactElement => {
  switch (modality) {
    case 'video':
      return React.createElement(VideoIcon, { sx: { fontSize: 18 } });
    case 'audio':
      return React.createElement(PhoneIcon, { sx: { fontSize: 18 } });
    case 'in_person':
      return React.createElement(PersonIcon, { sx: { fontSize: 18 } });
    default:
      return React.createElement(EncounterIcon, { sx: { fontSize: 18 } });
  }
};

export const getModalityLabel = (modality: string | null | undefined): string => {
  switch (modality) {
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    case 'in_person':
      return 'In Person';
    default:
      return 'Unknown';
  }
};

// ---------------------------------------------------------------------------
// Ready-to-bill helpers
// ---------------------------------------------------------------------------

export const getReadyToBillColor = (status: string | null | undefined, theme: Theme): string => {
  switch (status) {
    case 'green':
      return theme.palette.success.main;
    case 'yellow':
      return theme.palette.warning.main;
    case 'red':
      return theme.palette.error.main;
    default:
      return theme.palette.grey[400];
  }
};

export const getReadyToBillLabel = (status: string | null | undefined): string => {
  switch (status) {
    case 'green':
      return 'Ready to bill';
    case 'yellow':
      return 'Needs review';
    case 'red':
      return 'Blocked';
    default:
      return 'Not assessed';
  }
};

// ---------------------------------------------------------------------------
// Status color mapping
// ---------------------------------------------------------------------------

export const getClaimStatusColor = (
  status: string | null | undefined,
): 'default' | 'success' | 'warning' | 'info' | 'error' | 'primary' => {
  switch (status) {
    case 'accepted':
    case 'paid':
      return 'success';
    case 'submitted':
    case 'acknowledged':
      return 'info';
    case 'draft':
      return 'default';
    case 'rejected':
    case 'failed':
    case 'denied':
      return 'error';
    case 'processing':
    case 'scrubbing':
    case 'partially_paid':
    case 'reversed':
      return 'warning';
    case 'ready':
      return 'success';
    default:
      return 'default';
  }
};
